import { Component, OnInit } from '@angular/core';
import { CardWizardService } from '../card-wizard.service';
import { MatDialogRef } from '@angular/material/dialog';
import { Card, isCorrelationMatrixCard, isPCACard } from 'src/generated-sources';
import { getBasicCardTitle } from '@features/eda/card-utils';
import produce from 'immer';

enum TestState {
    ONE_SAMPLE = 'ONE_SAMPLE',
    TWO_SAMPLE = 'TWO_SAMPLE',
    N_SAMPLE = 'N_SAMPLE',
    PAIRED = 'PAIRED',
}

export enum WizardState {
    ANALYSIS = 'ANALYSIS',
    TEST = 'TEST',
    FIT = 'FIT',
    VARIABLES = 'VARIABLES'
}

@Component({
    selector: 'new-card-modal',
    templateUrl: './new-card-modal.component.html',
    styleUrls: [
        '../../../shared-styles/card-wizard.less',
        './new-card-modal.component.less'
    ],
    providers: [CardWizardService]
})
export class NewCardModalComponent implements OnInit {
    state = WizardState.ANALYSIS;
    previousState = WizardState.ANALYSIS;
    WizardState = WizardState;
    selectedTest = TestState.ONE_SAMPLE;
    TestState = TestState;
    stateName = {
        [WizardState.ANALYSIS]: 'Select a card type',
        [WizardState.TEST]: 'Statistical Tests',
        [WizardState.FIT]: 'Fit curves & distributions',
        [WizardState.VARIABLES]: '',
    };
    title: string = this.stateName[this.state];

    params: any = {};
    changedParamsValid = true;
    changedParams?: Card;

    constructor(
        private cardWizardService: CardWizardService,
        private dialogRef: MatDialogRef<NewCardModalComponent>
    ) {
    }

    ngOnInit() {
    }

    setCardType(type: Card['type']) {
        this.params = this.cardWizardService.getCardDefault(type);
        this.title = getBasicCardTitle(this.params);
        this.goToState(WizardState.VARIABLES);
    }

    goToState(state: WizardState) {
        this.previousState = this.state;
        this.state = state;

        if (this.state !== WizardState.VARIABLES) {
            this.title = this.stateName[this.state];
        }
    }

    goToPreviousState(state: WizardState) {
        let previousState: WizardState = this.previousState;

        switch (state) {
            case WizardState.FIT:
            case WizardState.TEST:
                previousState = WizardState.ANALYSIS;
                break;
        }

        this.goToState(previousState);
    }

    setTestType(type: TestState) {
        this.selectedTest = type;
    }

    changeValidity(validity: boolean) {
        this.changedParamsValid = validity;
    }

    changeParams(params: Card) {
        this.changedParams = params;
    }

    dismiss() {
        this.dialogRef.close('');
    }

    get canSave() {
        return this.changedParams && this.changedParamsValid;
    }

    save() {
        if (this.canSave) {
            this.dialogRef.close({
                card: withSmartCreationParams(this.changedParams!)
            });
        }
    }
}

// Not all parameters are configurable in card creation modal
// This function overrides default parameters with smart values
function withSmartCreationParams(newCard: Card): Card {
    // Hide values when the heatmap is too large (unreadable)
    if (isCorrelationMatrixCard(newCard) || isPCACard(newCard)) {
        return produce(newCard, draft => {
            if (newCard.columns.length > 20) {
                draft.heatmapParams.showValues = false;
            }
        });
    }

    return newCard;
}
